// crewbit-admin/src/hooks/useUpdateUserSchedulePreferences.ts
//
// Schedule Redesign v1 — paired hook for update_user_schedule_preferences.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §8.4.

import { useCallback, useState } from 'react';
import { callScheduleRpc, type ScheduleRpcResult } from '../lib/scheduleRpc';

export type ScheduleZoomMobile = 'today' | 'day' | 'week' | 'month' | 'year';
export type ScheduleZoomDesktop = 'day' | 'week' | 'month' | 'year';
export type ScheduleLensMobile = 'calendar' | 'route';
export type ScheduleLensDesktop = 'calendar' | 'crew' | 'route';

/** Display-preference keys (M-Schedule-D0; extend additively — RPC merges via ||). */
export interface ScheduleDisplayPreferences {
  /** TodayViewSwitcher persistent toggle (mobile). */
  today_display_mode?: 'list' | 'grid';
  /** Day canvas tile padding. 'compact' = 44px min tiles; 'comfortable' = 64px (default). */
  day_density?: 'compact' | 'comfortable';
  /** Week strip days shown. '5_day' = Mon-Fri; '7_day' = Sun-Sat (default). */
  week_layout?: '5_day' | '7_day';
  /** Month cell rendering. 'dots' = compact dot row (default); 'pills' = legacy text pills. */
  month_dot_style?: 'dots' | 'pills';
  /** Today list filter — false hides completed entries (default true; show completed). */
  agenda_show_completed?: boolean;
  /** Route view — auto-reorder stops by nearest-neighbor proximity. Default false. */
  route_auto_optimize?: boolean;
}

export interface UpdateUserSchedulePreferencesInput {
  /** Set, clear, or omit mobile zoom default. */
  mobileZoom?: ScheduleZoomMobile | null;
  /** Set, clear, or omit desktop zoom default. */
  desktopZoom?: ScheduleZoomDesktop | null;
  /** Set, clear, or omit mobile lens default. */
  mobileLens?: ScheduleLensMobile | null;
  /** Set, clear, or omit desktop lens default. */
  desktopLens?: ScheduleLensDesktop | null;
  /** Partial display preferences object; only specified keys are merged server-side. */
  displayPreferences?: Partial<ScheduleDisplayPreferences>;
}

export type UpdateUserSchedulePreferencesResult = ScheduleRpcResult<{
  success: true;
  updated_fields: Record<string, unknown>;
}>;

export interface UseUpdateUserSchedulePreferencesReturn {
  mutate: (
    input: UpdateUserSchedulePreferencesInput,
  ) => Promise<UpdateUserSchedulePreferencesResult>;
  loading: boolean;
}

export function useUpdateUserSchedulePreferences(): UseUpdateUserSchedulePreferencesReturn {
  const [loading, setLoading] = useState<boolean>(false);

  const mutate = useCallback(
    async (
      input: UpdateUserSchedulePreferencesInput,
    ): Promise<UpdateUserSchedulePreferencesResult> => {
      setLoading(true);
      try {
        const updates: Record<string, unknown> = {};
        if ('mobileZoom' in input) updates.mobile_zoom = input.mobileZoom ?? null;
        if ('desktopZoom' in input) updates.desktop_zoom = input.desktopZoom ?? null;
        if ('mobileLens' in input) updates.mobile_lens = input.mobileLens ?? null;
        if ('desktopLens' in input) updates.desktop_lens = input.desktopLens ?? null;
        if ('displayPreferences' in input && input.displayPreferences) {
          updates.display_preferences = input.displayPreferences;
        }
        return await callScheduleRpc<{
          success: true;
          updated_fields: Record<string, unknown>;
        }>('update_user_schedule_preferences', { p_updates: updates });
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { mutate, loading };
}
